/**
 * PerformersPanel — organizer editor for the event's performer lineup.
 * Name + short bio + Instagram + image gallery per performer. Public page
 * grows a "Performers" tab and schema.org JSON-LD gains performer[] once
 * at least one is added.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mic2, Plus, Pencil, Trash2, Instagram, Loader2, X, ArrowUp, ArrowDown, ImagePlus, Megaphone, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });
const EMPTY = { name: '', bio: '', instagram: '', images: [] };
const TYPE_OPTIONS = [
  { value: 'dj', label: 'DJ' }, { value: 'band', label: 'Band' },
  { value: 'comedian', label: 'Comedian' }, { value: 'speaker', label: 'Speaker' },
  { value: 'artist', label: 'Artist' }, { value: 'musician', label: 'Musician' },
  { value: 'performer', label: 'Other' },
];

function InvitePromoterForm({ eventId, performer, onDone, onCancel }) {
  const [form, setForm] = useState({ email: '', performer_type: 'dj', commission_flat: '', commission_percent: '' });
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!form.email.includes('@')) { toast.error('Enter the performer\'s email'); return; }
    if (!Number(form.commission_flat) && !Number(form.commission_percent)) { toast.error('Set a commission ($ and/or %)'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/events/${eventId}/performer-invites`, {
        performer_id: performer.performer_id,
        email: form.email.trim(),
        performer_type: form.performer_type,
        commission_flat: Number(form.commission_flat) || 0,
        commission_percent: Number(form.commission_percent) || 0,
      }, auth());
      toast.success(`Promo invite sent to ${form.email.trim()}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
    setSending(false);
  };
  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2.5" data-testid={`invite-form-${performer.performer_id}`}>
      <p className="text-xs font-semibold">Invite {performer.name} to promote this event</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="Performer's email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
        <select value={form.performer_type} onChange={e => setForm({ ...form, performer_type: e.target.value })}
          className="h-10 px-3 rounded-md border border-border bg-background text-sm" data-testid="invite-type-select">
          {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Input placeholder="$ per ticket (e.g. 2)" type="number" min="0" step="0.5" value={form.commission_flat} onChange={e => setForm({ ...form, commission_flat: e.target.value })} data-testid="invite-flat-input" />
        <Input placeholder="% of sale (e.g. 10)" type="number" min="0" max="50" step="0.5" value={form.commission_percent} onChange={e => setForm({ ...form, commission_percent: e.target.value })} data-testid="invite-percent-input" />
      </div>
      <p className="text-[10px] text-muted-foreground">They'll get an email with their tracking link offer; accepting opts them into the street-team wallet with this commission and creates their public performer page.</p>
      <div className="flex gap-2">
        <Button size="sm" className="rounded-full font-semibold" disabled={sending} onClick={send} data-testid="invite-send-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send invite</>}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function PerformersPanel({ eventId }) {
  const [performers, setPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed, {..., performer_id?} = open
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [invites, setInvites] = useState({});
  const [inviteFor, setInviteFor] = useState(null);
  const fileRef = useRef(null);

  const fetchPerformers = async () => {
    try {
      const res = await axios.get(`${API}/hosted-events/${eventId}`, auth());
      const ev = res.data.event || res.data;
      setPerformers(ev.performers || []);
      const inv = await axios.get(`${API}/events/${eventId}/performer-invites`, auth());
      const map = {};
      (inv.data.invites || []).forEach(i => { if (!map[i.performer_id]) map[i.performer_id] = i; });
      setInvites(map);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { fetchPerformers(); /* eslint-disable-next-line */ }, [eventId]);

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
        });
        urls.push(`${API}/files/${res.data.path}`);
      }
      setForm(f => ({ ...f, images: [...f.images, ...urls].slice(0, 12) }));
      toast.success(`${urls.length} image${urls.length !== 1 ? 's' : ''} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Performer name is required'); return; }
    setSaving(true);
    try {
      if (form.performer_id) {
        await axios.put(`${API}/hosted-events/${eventId}/performers/${form.performer_id}`, form, auth());
        toast.success('Performer updated');
      } else {
        await axios.post(`${API}/hosted-events/${eventId}/performers`, form, auth());
        toast.success(`${form.name} added to the lineup`);
      }
      setForm(null);
      fetchPerformers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/hosted-events/${eventId}/performers/${p.performer_id}`, auth());
      toast.success(`${p.name} removed`);
      fetchPerformers();
    } catch { toast.error('Delete failed'); }
  };

  const move = async (idx, dir) => {
    const next = [...performers];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    setPerformers(next);
    try {
      await axios.put(`${API}/hosted-events/${eventId}/performers-order`,
        { performer_ids: next.map(p => p.performer_id) }, auth());
    } catch { fetchPerformers(); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>;

  return (
    <div className="space-y-4" data-testid="performers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold flex items-center gap-2"><Mic2 className="h-4 w-4 text-fuchsia-400" /> Performers</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Adding a performer creates a Performers tab on your public event page and boosts SEO (schema.org performer data).
          </p>
        </div>
        {!form && (
          <Button size="sm" className="rounded-full font-semibold" onClick={() => setForm({ ...EMPTY })} data-testid="add-performer-btn">
            <Plus className="h-4 w-4 mr-1" /> Add performer
          </Button>
        )}
      </div>

      {form && (
        <Card className="border-fuchsia-500/30">
          <CardContent className="p-4 space-y-3" data-testid="performer-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="DJ Nova" data-testid="performer-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Instagram</Label>
                <Input value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })} placeholder="@djnova or full URL" data-testid="performer-instagram-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Short details</Label>
              <Textarea rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="Denver's favorite open-format DJ…" data-testid="performer-bio-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Image gallery</Label>
              <div className="flex flex-wrap gap-2">
                {form.images.map((url, i) => (
                  <div key={url + i} className="relative h-16 w-16 rounded-md overflow-hidden border border-border group">
                    <img src={url} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, x) => x !== i) }))}
                      className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`performer-image-remove-${i}`}>
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-16 w-16 rounded-md border border-dashed border-fuchsia-500/40 flex items-center justify-center hover:bg-fuchsia-500/5 transition-colors"
                  data-testid="performer-image-upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin text-fuchsia-400" /> : <ImagePlus className="h-4 w-4 text-fuchsia-400" />}
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                  onChange={e => e.target.files?.length && uploadImages(e.target.files)} data-testid="performer-image-input" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={save} disabled={saving} className="rounded-full font-semibold" data-testid="performer-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.performer_id ? 'Save changes' : 'Add performer'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setForm(null)} data-testid="performer-cancel-btn">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {performers.length === 0 && !form ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="performers-empty">
          No performers yet — add your lineup to unlock the Performers tab on the public page.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {performers.map((p, idx) => {
            const inv = invites[p.performer_id];
            return (
            <div key={p.performer_id} className="space-y-2">
            <Card className="border-border" data-testid={`performer-row-${p.performer_id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className="h-12 w-12 rounded-md object-cover flex-shrink-0" />
                ) : (
                  <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0"><Mic2 className="h-5 w-5 text-muted-foreground" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {p.images?.length || 0} photo{(p.images?.length || 0) !== 1 ? 's' : ''}{p.bio ? ` · ${p.bio}` : ''}
                  </p>
                </div>
                {p.instagram && (
                  <a href={p.instagram} target="_blank" rel="noopener noreferrer" className="text-fuchsia-400 hover:text-fuchsia-300" data-testid={`performer-ig-${p.performer_id}`}>
                    <Instagram className="h-4 w-4" />
                  </a>
                )}
                {inv?.status === 'accepted' ? (
                  <a href={inv.profile_url || '#'} target="_blank" rel="noopener noreferrer" data-testid={`performer-promoting-${p.performer_id}`}>
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      Promoting <ExternalLink className="h-2.5 w-2.5" />
                    </span>
                  </a>
                ) : inv ? (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`performer-invited-${p.performer_id}`}>Invited</span>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-full border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                    onClick={() => setInviteFor(inviteFor === p.performer_id ? null : p.performer_id)} data-testid={`performer-invite-btn-${p.performer_id}`}>
                    <Megaphone className="h-3 w-3 mr-1" /> Invite to promote
                  </Button>
                )}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="h-7 w-7" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`performer-up-${p.performer_id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" disabled={idx === performers.length - 1} onClick={() => move(idx, 1)} data-testid={`performer-down-${p.performer_id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setForm({ ...EMPTY, ...p })} data-testid={`performer-edit-${p.performer_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => remove(p)} data-testid={`performer-delete-${p.performer_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
            {inviteFor === p.performer_id && (
              <InvitePromoterForm eventId={eventId} performer={p}
                onDone={() => { setInviteFor(null); fetchPerformers(); }}
                onCancel={() => setInviteFor(null)} />
            )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
